/**
 * One person's record from a Versi directory answer ("show student with GR number 062273").
 *
 * When a looked-up record has an empty profile field, node-api skips the model and writes the row
 * itself as one sentence (`profileSentence` in `attendance-sql-agent/directoryAnswer.js`):
 *
 *   `Adam Saad: student no is 062273; date of birth is 2020-08-16; mother name is not recorded.`
 *
 * The answer carries no structured rows, so the sentence is read back into fields here and drawn
 * as a profile card instead of a wall of text. Anything that does not have that exact shape — a
 * line break, fewer than three `label is value` parts, a label that is not a lower-case column
 * name — is left to the normal prose rendering.
 */

export type DirectoryProfileField = {label: string; value: string}

export type DirectoryProfile = {
  name: string
  /** Fields with a value, in the order the backend sent them. */
  fields: DirectoryProfileField[]
  /** Labels of the fields the backend wrote as "not recorded". */
  notRecorded: string[]
}

const NOT_RECORDED = 'not recorded'

/** Fewer parts than this reads as an ordinary sentence, not a record. */
const MIN_FIELDS = 3

/** The backend lower-cases the column alias it writes as a label ("student no", "date of birth"). */
const LABEL = /^[a-z][a-z0-9 /&'().-]{0,40}$/

const MAX_NAME_LENGTH = 120

/** The profile in a directory answer, or null when the text is anything else. */
export const parseDirectoryProfile = (text: string): DirectoryProfile | null => {
  const trimmed = String(text ?? '').trim()
  if (!trimmed || trimmed.includes('\n')) return null

  const colon = trimmed.indexOf(': ')
  if (colon <= 0 || colon > MAX_NAME_LENGTH) return null
  const name = trimmed.slice(0, colon).trim()
  const body = trimmed.slice(colon + 2).replace(/\.$/, '')

  const pairs: DirectoryProfileField[] = []
  for (const part of body.split('; ')) {
    const at = part.indexOf(' is ')
    const label = at > 0 ? part.slice(0, at).trim() : ''
    if (label && LABEL.test(label)) {
      pairs.push({label, value: part.slice(at + 4).trim()})
    } else if (pairs.length) {
      // A value with "; " in it (an address) — the part belongs to the field before it.
      pairs[pairs.length - 1].value += `; ${part}`
    } else {
      return null
    }
  }
  if (pairs.length < MIN_FIELDS) return null

  return {
    name,
    fields: pairs.filter((pair) => pair.value && pair.value.toLowerCase() !== NOT_RECORDED),
    notRecorded: pairs
      .filter((pair) => !pair.value || pair.value.toLowerCase() === NOT_RECORDED)
      .map((pair) => pair.label),
  }
}

/** "date of birth" → "Date of birth". */
export const profileLabel = (label: string): string =>
  label ? label.charAt(0).toUpperCase() + label.slice(1) : label

/** The record's status ("profile status is Active"), shown as a badge beside the name. */
export const isStatusField = (field: DirectoryProfileField): boolean =>
  /\bstatus$/.test(field.label)

/**
 * A bare `YYYY-MM-DD` value as a local date, or null. Built from its parts rather than
 * `new Date(value)`, which reads it as UTC midnight and shows the day before west of Greenwich.
 */
export const profileDate = (value: string): Date | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime()) ? null : date
}
